import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OrderFulfillment() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [deliveries, setDeliveries] = useState([]);
  const [pagination, setPagination] = useState({ current_page: 1, last_page: 1 });
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [page, setPage] = useState(1);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Modals
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);

  // Create form
  const [orderId, setOrderId] = useState('');
  const [confirmedOrders, setConfirmedOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({ delivery_date: new Date().toISOString().split('T')[0], delivery_address: '', delivered_by_type: '', delivered_by_name: '', delivery_notes: '', auto_confirm: true });
  const [deliveryLines, setDeliveryLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/orders/deliveries/list?page=${page}`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (dateFrom) url += `&date_from=${dateFrom}`;
      if (dateTo) url += `&date_to=${dateTo}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setDeliveries(Array.isArray(json.data) ? json.data : []);
      setPagination({ current_page: json.current_page, last_page: json.last_page });
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [companyId, page, search, statusFilter, dateFrom, dateTo]);

  useEffect(() => { fetchDeliveries(); }, [fetchDeliveries]);

  const fmt = (v) => Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

  // ── Load confirmed orders for delivery creation ──
  const fetchConfirmedOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders?system_state=confirmed&per_page=100`, { headers: getAuthHeaders() });
      if (!res.ok) return;
      const json = await res.json();
      setConfirmedOrders(Array.isArray(json.data) ? json.data : []);
    } catch (e) {}
  }, [companyId]);

  // ── Load order detail for delivery form ──
  const loadOrderForDelivery = async (oId) => {
    setOrderId(oId);
    if (!oId) { setSelectedOrder(null); setDeliveryLines([]); return; }
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/${oId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      const order = json.order;
      setSelectedOrder(order);
      setDeliveryForm(f => ({ ...f, delivery_address: order.delivery_address || '' }));
      // Build delivery lines from product lines with remaining qty
      const productLines = (order.lines || []).filter(l => l.line_type === 'product' && l.remaining_qty > 0);
      setDeliveryLines(productLines.map(l => ({
        order_line_id: l.id,
        description: l.description,
        ordered_qty: l.ordered_qty,
        delivered_qty: l.delivered_qty,
        remaining_qty: l.remaining_qty,
        quantity_delivered: l.remaining_qty, // default to full remaining
      })));
    } catch (e) { alert('Error loading order'); }
  };

  // ── Create Delivery ──
  const handleCreate = async () => {
    if (!orderId) return alert('Select an order');
    const validLines = deliveryLines.filter(l => l.quantity_delivered > 0);
    if (validLines.length === 0) return alert('Enter delivery quantities');
    setSaving(true);
    try {
      const payload = {
        order_id: Number(orderId),
        ...deliveryForm,
        lines: validLines.map(l => ({ order_line_id: l.order_line_id, quantity_delivered: l.quantity_delivered })),
      };
      const res = await fetch(`${API}/companies/${companyId}/orders/deliveries`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message || 'Error'); setSaving(false); return; }
      setShowCreate(false); setOrderId(''); setSelectedOrder(null); setDeliveryLines([]);
      fetchDeliveries();
    } catch (e) { alert('Error creating delivery'); }
    setSaving(false);
  };

  // ── Confirm Delivery ──
  const handleConfirm = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/deliveries/${id}/confirm`, {
        method: 'POST', headers: getAuthHeaders(),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      fetchDeliveries();
    } catch (e) { alert('Error'); }
  };

  // ── Reverse Delivery ──
  const handleReverse = async (id) => {
    if (!window.confirm('Reverse this delivery? This will undo fulfilled quantities.')) return;
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/deliveries/${id}/reverse`, {
        method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: 'Manual reversal' }),
      });
      if (!res.ok) { const err = await res.json(); alert(err.message); return; }
      fetchDeliveries();
    } catch (e) { alert('Error'); }
  };

  // ── View Detail ──
  const openDetail = async (id) => {
    try {
      const res = await fetch(`${API}/companies/${companyId}/orders/deliveries/${id}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed');
      const json = await res.json();
      setShowDetail(json.delivery);
    } catch (e) { alert('Error loading delivery'); }
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>🚚 Fulfillment & Deliveries</h2>
        <button onClick={() => { fetchConfirmedOrders(); setShowCreate(true); }} style={btnPrimary}>+ New Delivery</button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Search deliveries..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} style={inputStyle} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={selectStyle}>
          <option value="">All Statuses</option>
          <option value="draft">Draft</option><option value="confirmed">Confirmed</option><option value="reversed">Reversed</option>
        </select>
        <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }} style={inputStyle} placeholder="From" />
        <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }} style={inputStyle} placeholder="To" />
      </div>

      {/* Table */}
      {initialLoading ? <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>{['Delivery #','Order #','Client','Date','Delivered By','Status','Items','Actions'].map(h =>
                <th key={h} style={thStyle}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {deliveries.map(d => (
                <tr key={d.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: '#3b82f6' }}>{d.delivery_number}</td>
                  <td style={tdStyle}>{d.order?.order_number || '—'}</td>
                  <td style={tdStyle}>{d.client_name || d.order?.client_name || '—'}</td>
                  <td style={tdStyle}>{d.delivery_date?.split('T')[0]}</td>
                  <td style={tdStyle}>{d.delivered_by_name || '—'}</td>
                  <td style={tdStyle}><Badge value={d.status} /></td>
                  <td style={tdStyle}>{d.lines?.length || 0} item(s)</td>
                  <td style={tdStyle}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => openDetail(d.id)} style={btnSm}>View</button>
                      {d.status === 'draft' && <button onClick={() => handleConfirm(d.id)} style={{ ...btnSm, background: '#3b82f6', color: '#fff' }}>Confirm</button>}
                      {d.status === 'confirmed' && <button onClick={() => handleReverse(d.id)} style={{ ...btnSm, background: '#ef4444', color: '#fff' }}>Reverse</button>}
                    </div>
                  </td>
                </tr>
              ))}
              {deliveries.length === 0 && <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No deliveries found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {pagination.last_page > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <button disabled={page <= 1} onClick={() => setPage(page - 1)} style={btnSm}>← Prev</button>
          <span style={{ padding: '6px 12px', fontSize: 13 }}>Page {pagination.current_page} of {pagination.last_page}</span>
          <button disabled={page >= pagination.last_page} onClick={() => setPage(page + 1)} style={btnSm}>Next →</button>
        </div>
      )}

      {/* ═══ Create Delivery Modal ═══ */}
      {showCreate && (
        <Modal title="New Delivery" onClose={() => { setShowCreate(false); setOrderId(''); setSelectedOrder(null); setDeliveryLines([]); }} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <label style={labelStyle}>Order *
              <select value={orderId} onChange={e => loadOrderForDelivery(e.target.value)} style={inputStyle}>
                <option value="">— Select Confirmed Order —</option>
                {confirmedOrders.map(o => <option key={o.id} value={o.id}>{o.order_number} — {o.client_name || 'No client'}</option>)}
              </select>
            </label>
            <label style={labelStyle}>Delivery Date
              <input type="date" value={deliveryForm.delivery_date} onChange={e => setDeliveryForm({ ...deliveryForm, delivery_date: e.target.value })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Delivery Address
              <input value={deliveryForm.delivery_address} onChange={e => setDeliveryForm({ ...deliveryForm, delivery_address: e.target.value })} style={inputStyle} />
            </label>
            <label style={labelStyle}>Delivered By
              <input value={deliveryForm.delivered_by_name} onChange={e => setDeliveryForm({ ...deliveryForm, delivered_by_name: e.target.value })} style={inputStyle} placeholder="Name / Team" />
            </label>
            <label style={labelStyle}>Delivered By Type
              <select value={deliveryForm.delivered_by_type} onChange={e => setDeliveryForm({ ...deliveryForm, delivered_by_type: e.target.value })} style={inputStyle}>
                <option value="">— Select —</option>
                <option value="employee">Employee</option><option value="internal_team">Internal Team</option><option value="external">External</option>
              </select>
            </label>
            <label style={labelStyle}>Notes
              <input value={deliveryForm.delivery_notes} onChange={e => setDeliveryForm({ ...deliveryForm, delivery_notes: e.target.value })} style={inputStyle} />
            </label>
          </div>

          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, marginBottom: 16 }}>
            <input type="checkbox" checked={deliveryForm.auto_confirm} onChange={e => setDeliveryForm({ ...deliveryForm, auto_confirm: e.target.checked })} />
            Auto-confirm delivery (immediately update quantities)
          </label>

          {/* Product Lines */}
          {deliveryLines.length > 0 ? (
            <>
              <h4 style={{ fontSize: 14, fontWeight: 600, margin: '12px 0 8px' }}>Product Lines to Deliver</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr>{['Product','Ordered','Delivered','Remaining','Qty to Deliver'].map(h =>
                    <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {deliveryLines.map((l, i) => (
                    <tr key={i}>
                      <td style={tdSm}>{l.description}</td>
                      <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.ordered_qty)}</td>
                      <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.delivered_qty)}</td>
                      <td style={{ ...tdSm, textAlign: 'right', fontWeight: 600, color: '#f59e0b' }}>{fmt(l.remaining_qty)}</td>
                      <td style={tdSm}>
                        <input type="number" min="0" max={l.remaining_qty} value={l.quantity_delivered}
                          onChange={e => {
                            const n = [...deliveryLines];
                            n[i] = { ...n[i], quantity_delivered: Number(e.target.value) };
                            setDeliveryLines(n);
                          }}
                          style={{ ...inputStyle, width: 80, textAlign: 'right' }} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : selectedOrder ? (
            <div style={{ padding: 20, textAlign: 'center', color: '#9ca3af', background: '#f9fafb', borderRadius: 8 }}>
              No product lines with remaining quantity
            </div>
          ) : null}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <button onClick={() => { setShowCreate(false); setOrderId(''); setSelectedOrder(null); setDeliveryLines([]); }} style={btnSm}>Cancel</button>
            <button onClick={handleCreate} disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Create Delivery'}</button>
          </div>
        </Modal>
      )}

      {/* ═══ Detail Modal ═══ */}
      {showDetail && (
        <Modal title={`Delivery ${showDetail.delivery_number}`} onClose={() => setShowDetail(null)} wide>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><span style={detailLabel}>Order</span><span style={detailVal}>{showDetail.order?.order_number}</span></div>
            <div><span style={detailLabel}>Client</span><span style={detailVal}>{showDetail.client_name || '—'}</span></div>
            <div><span style={detailLabel}>Date</span><span style={detailVal}>{showDetail.delivery_date?.split('T')[0]}</span></div>
            <div><span style={detailLabel}>Status</span><Badge value={showDetail.status} /></div>
            <div><span style={detailLabel}>Delivered By</span><span style={detailVal}>{showDetail.delivered_by_name || '—'} {showDetail.delivered_by_type ? `(${showDetail.delivered_by_type})` : ''}</span></div>
            <div><span style={detailLabel}>Created By</span><span style={detailVal}>{showDetail.created_by_user?.name || '—'}</span></div>
            {showDetail.delivery_address && <div style={{ gridColumn: '1 / -1' }}><span style={detailLabel}>Address</span><span style={detailVal}>{showDetail.delivery_address}</span></div>}
            {showDetail.delivery_notes && <div style={{ gridColumn: '1 / -1' }}><span style={detailLabel}>Notes</span><span style={detailVal}>{showDetail.delivery_notes}</span></div>}
          </div>

          <h4 style={{ fontSize: 14, fontWeight: 600, margin: '12px 0 8px' }}>Delivery Lines</h4>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>{['Product','Ordered','Prev Delivered','This Delivery','Remaining After'].map(h =>
                <th key={h} style={{ ...thStyle, fontSize: 11, padding: '6px 8px' }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {Array.isArray(showDetail.lines) && showDetail.lines.map(l => (
                <tr key={l.id}>
                  <td style={tdSm}>{l.product_name}</td>
                  <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.ordered_qty)}</td>
                  <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.previously_delivered_qty)}</td>
                  <td style={{ ...tdSm, textAlign: 'right', fontWeight: 600, color: '#10b981' }}>{fmt(l.quantity_delivered)}</td>
                  <td style={{ ...tdSm, textAlign: 'right' }}>{fmt(l.remaining_after)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            {showDetail.status === 'draft' && <button onClick={() => { handleConfirm(showDetail.id); setShowDetail(null); }} style={{ ...btnPrimary }}>Confirm</button>}
            {showDetail.status === 'confirmed' && <button onClick={() => { handleReverse(showDetail.id); setShowDetail(null); }} style={{ ...btnPrimary, background: '#ef4444' }}>Reverse</button>}
            <button onClick={() => setShowDetail(null)} style={btnSm}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Modal({ title, children, onClose, wide }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center',
      justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: wide ? 800 : 500,
        maxHeight: '90vh', overflow: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}
        onClick={e => e.stopPropagation()}>
        <h3 style={{ margin: '0 0 16px', fontSize: 18, fontWeight: 700, color: '#1a1a2e' }}>{title}</h3>
        {children}
      </div>
    </div>
  );
}

function Badge({ value }) {
  const colors = {
    draft: '#9ca3af', confirmed: '#3b82f6', reversed: '#ef4444',
    not_started: '#9ca3af', partially_delivered: '#f59e0b', delivered: '#10b981', overdue: '#ef4444',
  };
  const c = colors[value] || '#6b7280';
  return <span style={{ display: 'inline-block', padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: c + '18', color: c }}>{(value || '').replace(/_/g, ' ')}</span>;
}

const btnPrimary = { padding: '8px 20px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSm = { padding: '4px 10px', borderRadius: 6, border: '1px solid #d1d5db', background: '#fff', fontSize: 12, cursor: 'pointer' };
const inputStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, width: '100%', boxSizing: 'border-box' };
const selectStyle = { padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, background: '#fff' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 500, color: '#374151' };
const thStyle = { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #e5e7eb', fontSize: 12, fontWeight: 600, color: '#6b7280', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', borderBottom: '1px solid #f3f4f6', color: '#374151', whiteSpace: 'nowrap' };
const tdSm = { padding: '6px 8px', borderBottom: '1px solid #f3f4f6', color: '#374151' };
const detailLabel = { display: 'block', fontSize: 11, fontWeight: 600, color: '#9ca3af', marginBottom: 2 };
const detailVal = { fontSize: 14, color: '#374151' };
